import { createDateFormatter, type TimezoneMode } from "@nexus/ui";
import type { RecentResource } from "@/contexts/AppContext";
import type { DashboardRecord } from "@/lib/dashboard/types";
import type { Tenant } from "@/types";

export {
  dashboardTenantIds,
  dashboardTenants,
  dashboardWidgets,
} from "@/lib/dashboard/dashboardTenants";

/** Recently opened dashboards Home lists: one line. */
export const HOME_LIST_SIZE = 4;
/**
 * Dashboards Home shows at once: three rows of three. Up to this many, every one
 * shows; beyond, one place goes to the way to the full list ("View all N").
 */
export const HOME_FEATURED_SIZE = 9;
export const DASHBOARD_RECENT_KIND = "dashboard";

export interface RecentDashboard {
  dashboard: DashboardRecord;
  visitedAt: number;
}

export interface HomeDashboards {
  /** The person's pinned dashboards, most recent pin first. */
  pinned: DashboardRecord[];
  /**
   * Every saved dashboard in Home's order: the pinned ones first (in pin order),
   * then the most recently updated, regardless of ownership.
   */
  ordered: DashboardRecord[];
  /**
   * What Home shows at once: all of `ordered` when they fit
   * (`HOME_FEATURED_SIZE`), otherwise the first ones, leaving a place for the
   * way to the full list.
   */
  featured: DashboardRecord[];
  /** Opened lately, most recent first. Only dashboards still listed for the person. */
  recent: RecentDashboard[];
  /** Saved dashboards the person can open. */
  total: number;
}

/** What Home offers from the dashboards list and the browser's recents. Drafts are left out. */
export function selectHomeDashboards(
  dashboards: readonly DashboardRecord[],
  recents: readonly RecentResource[],
  pinnedIds: readonly string[] = [],
): HomeDashboards {
  const saved = dashboards.filter((dashboard) => dashboard.state !== "draft");
  const byId = new Map(saved.map((dashboard) => [dashboard.id, dashboard]));
  const latest = [...saved].sort(
    (left, right) =>
      right.updated_at.localeCompare(left.updated_at) || left.name.localeCompare(right.name),
  );

  const pinned = pinnedIds.flatMap((id) => {
    const dashboard = byId.get(id);
    return dashboard ? [dashboard] : [];
  });

  const pinnedSet = new Set(pinned.map((dashboard) => dashboard.id));
  const ordered = [...pinned, ...latest.filter((dashboard) => !pinnedSet.has(dashboard.id))];

  return {
    pinned,
    ordered,
    featured:
      ordered.length <= HOME_FEATURED_SIZE ? ordered : ordered.slice(0, HOME_FEATURED_SIZE - 1),
    recent: recents
      .filter((resource) => resource.kind === DASHBOARD_RECENT_KIND)
      .sort((a, b) => b.visitedAt - a.visitedAt)
      .flatMap((resource) => {
        const dashboard = byId.get(resource.id);
        return dashboard ? [{ dashboard, visitedAt: resource.visitedAt }] : [];
      })
      .slice(0, HOME_LIST_SIZE),
    total: saved.length,
  };
}

// ─── Perimeter ────────────────────────────────────────────────────────────────

export interface Perimeter {
  /** The customer every readable tenant belongs to, when they share one. */
  organization: string | null;
  tenants: Tenant[];
  /** The account reads every tenant: its data is not bounded, and its scopes are global. */
  allTenants: boolean;
  /** Data, tenants and dashboards are bounded by the account's scopes (as the API says). */
  limited: boolean;
}

export function needsAccessConfiguration(scopes: readonly string[], perimeter: Perimeter): boolean {
  return perimeter.limited && scopes.length === 0 && perimeter.tenants.length === 0;
}

/**
 * What the account covers, from what the API returns: for an account whose data is
 * limited to its scopes, the tenants list already holds only the tenants in scope.
 */
export function describePerimeter(
  scopes: readonly string[],
  tenants: readonly Tenant[],
  limited = false,
): Perimeter {
  const customers = new Set(tenants.map((tenant) => tenant.customer).filter(Boolean));
  return {
    organization: customers.size === 1 ? ([...customers][0] ?? null) : null,
    tenants: [...tenants].sort((a, b) => a.tenant_label.localeCompare(b.tenant_label)),
    allTenants: !limited && scopes.some((scope) => scope.startsWith("global:")),
    limited,
  };
}

// ─── Dashboard contents ───────────────────────────────────────────────────────

// ─── Dates ────────────────────────────────────────────────────────────────────

/** "5 minutes ago", "yesterday", "3 weeks ago"; the date beyond a month. */
export function formatRelativeTime(
  value: number | string,
  now = Date.now(),
  timezone: TimezoneMode = "utc",
): string {
  const time = typeof value === "number" ? value : Date.parse(value);
  const seconds = (time - now) / 1000;
  if (Math.abs(seconds) < 60) return "just now";
  const format = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  const minutes = seconds / 60;
  if (Math.abs(minutes) < 60) return format.format(Math.round(minutes), "minute");
  const hours = minutes / 60;
  if (Math.abs(hours) < 24) return format.format(Math.round(hours), "hour");
  const days = hours / 24;
  if (Math.abs(days) < 7) return format.format(Math.round(days), "day");
  const weeks = days / 7;
  if (Math.abs(weeks) < 5) return format.format(Math.round(weeks), "week");
  return createDateFormatter(timezone, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(new Date(time));
}
